/**
 * Extended Navigator interface for iOS standalone detection
 */
interface ExtendedNavigator extends Navigator {
  standalone?: boolean;
}

/**
 * Browser's BeforeInstallPromptEvent interface for PWA install prompts
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export async function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/',
      });

      console.log('✅ Service Worker registered successfully:', registration.scope);

      // Check for updates periodically
      setInterval(() => {
        registration.update();
      }, 60000); // Check every minute

      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker available - notification will be shown by PWAInstallPrompt component
              console.log('📦 New service worker available (admin users will be notified)');
            }
          });
        }
      });

      return registration;
    } catch (error) {
      console.error('❌ Service Worker registration failed:', error);
    }
  } else {
    console.log('⚠️ Service Workers not supported in this browser');
  }
}

// Request notification permission
export async function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  }
  return Notification.permission;
}

// Check if app is installed (running in standalone mode)
export function isAppInstalled(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as ExtendedNavigator).standalone === true ||
         document.referrer.includes('android-app://');
}

// Show install prompt
export function showInstallPrompt(deferredPrompt: BeforeInstallPromptEvent) {
  if (!deferredPrompt) return;

  // Check if suppressed
  const suppressUntil = localStorage.getItem('pwa-suppress-until');
  if (suppressUntil && Date.now() < parseInt(suppressUntil)) {
    console.log('🚫 PWA install prompt suppressed until', new Date(parseInt(suppressUntil)).toLocaleString());
    return;
  }

  const installBanner = document.createElement('div');
  installBanner.style.cssText = `
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(135deg, #f97316 0%, #fb923c 100%);
    color: white;
    padding: 16px 24px;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(249, 115, 22, 0.3);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: system-ui, -apple-system, sans-serif;
    max-width: 90%;
    min-width: 300px;
  `;

  installBanner.innerHTML = `
    <div style="display: flex; align-items: center; gap: 12px;">
      <span style="font-size: 32px;">📱</span>
      <div>
        <div style="font-weight: 700; font-size: 16px;">Install Dough Man OMS</div>
        <div style="font-size: 13px; opacity: 0.9;">Access the app faster with one tap!</div>
      </div>
    </div>
    <div style="display: flex; gap: 8px;">
      <button id="install-btn" style="
        flex: 1;
        background: white;
        color: #f97316;
        border: none;
        padding: 10px 20px;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        font-size: 14px;
      ">Install</button>
      <button id="dismiss-install-btn" style="
        background: transparent;
        color: white;
        border: 2px solid white;
        padding: 10px 20px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 14px;
      ">Not Now</button>
    </div>
  `;

  document.body.appendChild(installBanner);

  document.getElementById('install-btn')?.addEventListener('click', async () => {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to install prompt: ${outcome}`);
    installBanner.remove();
  });

  document.getElementById('dismiss-install-btn')?.addEventListener('click', () => {
    // Suppress for 24 hours
    const suppressUntil = Date.now() + 24 * 60 * 60 * 1000;
    localStorage.setItem('pwa-suppress-until', suppressUntil.toString());
    console.log('🔇 Suppressing PWA install prompt for 24 hours');
    installBanner.remove();
  });
}
